import React from 'react';
import { FileText, Activity, Stethoscope, Lock, Database } from 'lucide-react';

const IsometricMedicalRecords = () => {
    // Standard Isometric rotation
    const sceneRotation = {
        transform: 'rotateX(55deg) rotateZ(45deg)',
        transformStyle: 'preserve-3d' as const
    };

    return (
        <div className="w-full h-full flex items-center justify-center scale-75 md:scale-100">
            <div
                className="relative w-[280px] h-[280px]"
                style={sceneRotation}
            >
                <style>
                    {`
                        @keyframes float-z {
                            0%, 100% { transform: translateZ(2px); }
                            50% { transform: translateZ(15px); }
                        }
                        @keyframes bar-1 { 0%, 100% { height: 30%; } 50% { height: 60%; } }
                        @keyframes bar-2 { 0%, 100% { height: 50%; } 50% { height: 30%; } }
                        @keyframes bar-3 { 0%, 100% { height: 65%; } 50% { height: 85%; } }
                        @keyframes bar-4 { 0%, 100% { height: 45%; } 50% { height: 70%; } }
                        @keyframes bar-5 { 0%, 100% { height: 75%; } 50% { height: 45%; } }
                    `}
                </style>
                {/* Main Base Layer */}
                <div
                    className="absolute top-0 left-0 w-full h-full"
                    style={{
                        transform: 'translateZ(0px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Shadow */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-900/40 blur-2xl"
                        style={{
                            transform: 'translateZ(-60px) translateY(40px) translateX(40px)',
                        }}
                    />

                    {/* Thickness Slices (Blue Base) */}
                    {Array.from({ length: 35 }).map((_, i) => (
                        <div
                            key={i}
                            className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-700"
                            style={{
                                transform: `translateZ(-${i + 1}px)`,
                            }}
                        />
                    ))}

                    {/* Top Face */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-600"
                        style={{
                            transform: 'translateZ(0px)',
                            boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.1)'
                        }}
                    >
                        {/* Subtle surface detail */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl" />

                        {/* Grid/Graph Paper Pattern for "Medical Chart" feel */}
                        <div className="absolute inset-0 opacity-20"
                            style={{
                                backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                                backgroundSize: '20px 20px'
                            }}
                        />
                    </div>
                </div>

                {/* Surface Elements (Flat on the board) */}

                {/* Database/Storage (Top Left) */}
                <div
                    className="absolute"
                    style={{
                        left: '20px',
                        top: '20px',
                        width: '80px',
                        height: '80px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d',
                        animation: 'float-z 4s ease-in-out infinite'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-full bg-slate-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-slate-600 rounded-full flex items-center justify-center shadow-sm border border-slate-500">
                        <Database className="w-10 h-10 text-slate-200" />
                    </div>
                </div>

                {/* Orange Card (Top Left) */}
                <div
                    className="absolute"
                    style={{
                        left: '150px',
                        top: '20px',
                        width: '90px',
                        height: '90px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d',
                        animationDelay: '0.5s'
                    }}
                >
                    {/* Card Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-orange-900" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-orange-600 rounded-2xl flex items-center justify-center shadow-sm border border-orange-400">
                        <FileText className="w-8 h-8 text-orange-200" />
                    </div>
                </div>

                {/* Security Lock (Bottom Right) */}
                <div
                    className="absolute"
                    style={{
                        right: '30px',
                        bottom: '30px',
                        width: '50px',
                        height: '50px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-emerald-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-emerald-500 rounded-xl flex items-center justify-center shadow-sm border border-emerald-400">
                        <Lock className="w-6 h-6 text-white" />
                    </div>
                </div>


                {/* Vitals Chart (Standing up) */}
                <div
                    className="absolute"
                    style={{
                        left: '120px',
                        top: '-120px',
                        width: '160px',
                        height: '160px',
                        transform: 'translateZ(60px) rotateZ(0deg) rotateX(-90deg)', // Standing up
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-teal-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}

                    {/* Front Face */}
                    <div className="absolute inset-0 bg-white rounded-xl shadow-xl border border-teal-200 overflow-hidden flex flex-col p-3">
                        {/* Header with SOAP text */}
                        <div className="flex justify-between items-center mb-2 border-b border-gray-100 pb-2">
                            <span className="text-teal-600 font-bold text-lg tracking-wider">SOAP</span>
                            <Activity className="w-4 h-4 text-teal-500" />
                        </div>

                        {/* Vitals Grid / Chart Lines */}
                        <div className="flex-1 space-y-2">
                            {/* Row 1 */}
                            <div className="flex items-center justify-between">
                                <div className="w-8 h-1.5 bg-gray-200 rounded"></div>
                                <div className="w-16 h-1.5 bg-teal-100 rounded"></div>
                            </div>
                            {/* Row 2 */}
                            <div className="flex items-center justify-between">
                                <div className="w-6 h-1.5 bg-gray-200 rounded"></div>
                                <div className="w-12 h-1.5 bg-teal-100 rounded"></div>
                            </div>
                            {/* Chart Area */}
                            <div className="mt-2 h-12 bg-gray-50 rounded border border-gray-100 relative overflow-hidden">
                                <div className="absolute bottom-0 left-0 w-full h-full flex items-end px-1 gap-1">
                                    <div className="w-1/5 bg-teal-200 rounded-t-sm" style={{ height: '33%', animation: 'bar-1 3s ease-in-out infinite' }}></div>
                                    <div className="w-1/5 bg-teal-300 rounded-t-sm" style={{ height: '50%', animation: 'bar-2 4s ease-in-out infinite' }}></div>
                                    <div className="w-1/5 bg-teal-400 rounded-t-sm" style={{ height: '66%', animation: 'bar-3 2.5s ease-in-out infinite' }}></div>
                                    <div className="w-1/5 bg-teal-300 rounded-t-sm" style={{ height: '50%', animation: 'bar-4 3.5s ease-in-out infinite' }}></div>
                                    <div className="w-1/5 bg-teal-500 rounded-t-sm" style={{ height: '75%', animation: 'bar-5 5s ease-in-out infinite' }}></div>
                                </div>
                            </div>
                            {/* Bottom Lines */}
                            <div className="flex gap-1 mt-1">
                                <div className="w-full h-1 bg-gray-100 rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>


                {/* MAIN ELEMENT: Medical Clipboard/Tablet */}
                <div
                    className="absolute"
                    style={{
                        left: '10%',
                        top: '100%',
                        width: '160px',
                        height: '160px',
                        marginLeft: '-90px',
                        marginTop: '-70px',
                        transform: 'translateZ(130px) rotateZ(-65deg) rotateX(-90deg) rotateY(25deg)', // Angled phone
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Clipboard Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-xl bg-gray-800" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}

                    {/* Clipboard Front */}
                    <div className="absolute inset-0 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col p-3">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-3 border-b pb-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <Stethoscope className="w-5 h-5 text-blue-500" />
                            </div>
                            <div className="w-16 h-2 bg-gray-200 rounded"></div>
                        </div>

                        {/* Content Lines (Simulating Text) */}
                        <div className="space-y-2">
                            <div className="flex gap-2">
                                <div className="w-1/3 h-2 bg-blue-100 rounded"></div>
                                <div className="w-2/3 h-2 bg-gray-100 rounded"></div>
                            </div>
                            <div className="flex gap-2">
                                <div className="w-1/4 h-2 bg-green-100 rounded"></div>
                                <div className="w-3/4 h-2 bg-gray-100 rounded"></div>
                            </div>
                            <div className="flex gap-2">
                                <div className="w-1/3 h-2 bg-yellow-100 rounded"></div>
                                <div className="w-2/3 h-2 bg-gray-100 rounded"></div>
                            </div>
                            <div className="flex gap-2">
                                <div className="w-1/4 h-2 bg-red-100 rounded"></div>
                                <div className="w-3/4 h-2 bg-gray-100 rounded"></div>
                            </div>
                        </div>

                        {/* Paw Print Stamp */}
                        <div className="absolute bottom-4 right-4 opacity-20 transform rotate-12">
                            <div className="w-10 h-10 bg-indigo-500 rounded-full"></div>
                        </div>
                    </div>

                    {/* Clip at top */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-16 h-6 bg-gray-300 rounded-t-lg border border-gray-400 shadow-sm"></div>
                </div>

            </div>
        </div>
    );
};



export default IsometricMedicalRecords;
